import {Injectable} from '@nestjs/common';
import {AIClient, ChatMessage, StreamCallback} from '../../infrastructure/llm/ai-client';
import {McpService} from '../mcp/mcp.service';

/**
 * Agent 核心调度服务
 * 负责多步推理循环、工具调用（Tool Call）解析及大模型协作
 */
@Injectable()
export class AgentService {
  constructor(private readonly mcpService: McpService) {}

  /**
   * 从大模型输出文本中提取标准的 JSON 工具调用块
   * @param text 大模型输出的文本
   */
  private extractToolJson(text: string): string | null {
    const codeBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/g;
    let match;
    while ((match = codeBlockRegex.exec(text)) !== null) {
      const jsonStr = this.findValidJson(match[1].trim());
      if (jsonStr) return jsonStr;
    }
    return this.findValidJson(text);
  }

  /**
   * 在文本中搜寻并解析合法的 JSON 对象
   * @param text 待解析文本
   */
  private findValidJson(text: string): string | null {
    let start = text.indexOf('{');
    while (start !== -1) {
      let braces = 0;
      let inStr = false;
      let esc = false;
      for (let i = start; i < text.length; i++) {
        const c = text[i];
        if (esc) {
          esc = false;
          continue;
        }
        if (c === '\\') {
          esc = true;
          continue;
        }
        if (c === '"') {
          inStr = !inStr;
          continue;
        }
        if (!inStr) {
          if (c === '{') braces++;
          else if (c === '}') {
            braces--;
            if (braces === 0) {
              const candidate = text.substring(start, i + 1);
              try {
                const parsed = JSON.parse(candidate);
                if (parsed && (parsed.name || parsed.tool)) {
                  if (!parsed.name && parsed.tool) parsed.name = parsed.tool;
                  parsed.arguments =
                    parsed.arguments || parsed.args || parsed.params || parsed.parameters || {};
                  return JSON.stringify(parsed);
                }
              } catch (e) {}
              break;
            }
          }
        }
      }
      start = text.indexOf('{', start + 1);
    }
    return null;
  }

  /**
   * 执行带有 MCP 工具自动调用的 Agent 任务循环
   * @param settings 大模型配置参数
   * @param userPrompt 用户输入的提示词
   * @param onChunk 流式数据块回调
   */
  public async runAgentTask(
    settings: any,
    userPrompt: string,
    onChunk?: StreamCallback,
  ): Promise<string> {
    const aiClient = new AIClient(settings);
    const toolDefs = this.mcpService.getToolDefinitions();
    const toolsDesc = toolDefs
      .map(t => `- ${t.name}: ${t.description} (参数: ${JSON.stringify(t.parameters)})`)
      .join('\n');

    const systemPrompt =
      `你是一个专业的 AI 编程助手，可以通过 JSON 调用工具完成任务。\n\n` +
      `可用工具列表：\n${toolsDesc}\n\n` +
      `【重要格式规范】：当需要调用工具时，请输出标准的 JSON 块，例如：\n` +
      `\`\`\`json\n{\n  "tool": "write_file",\n  "params": {\n    "path": "test.ts",\n    "content": "..."\n  }\n}\n\`\`\``;

    const messages: ChatMessage[] = [
      {role: 'system', content: systemPrompt},
      {role: 'user', content: userPrompt},
    ];

    let finalResponse = '';
    let steps = 0;
    const maxSteps = 6;

    while (steps < maxSteps) {
      steps++;
      const resText = await aiClient.chat(messages, chunk => {
        if (onChunk) onChunk(chunk);
      });

      finalResponse = resText;
      messages.push({role: 'assistant', content: resText});

      const toolJsonStr = this.extractToolJson(resText);
      if (!toolJsonStr) break;

      try {
        const toolCall = JSON.parse(toolJsonStr);
        const toolName = toolCall.name || toolCall.tool;
        const toolArgs =
          toolCall.arguments || toolCall.args || toolCall.params || toolCall.parameters || {};

        if (onChunk) {
          onChunk(`\n\n⚙️ **正在执行工具 [${toolName}]** (参数: ${JSON.stringify(toolArgs)})...\n`);
        }

        const toolResult = await this.mcpService.executeTool(toolName, toolArgs);

        if (onChunk) {
          onChunk(`📥 **工具返回结果**:\n\`\`\`\n${toolResult.slice(0, 500)}\n\`\`\`\n\n`);
        }

        messages.push({
          role: 'user',
          content: `[MCP 工具 "${toolName}" 执行结果]:\n${toolResult}`,
        });
      } catch (err: any) {
        messages.push({
          role: 'user',
          content: `[系统错误]: 解析或执行工具失败: ${err.message}`,
        });
      }
    }

    return finalResponse;
  }
}
